import * as React from 'react';
import { formatDateTimeRange } from '@/lib/site-time';
import type { AssistantProposedAction } from '@cdf/api-client-react';
import { CheckIcon, Loader2Icon, MicIcon, SendIcon, SquareIcon, Volume2Icon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAssistantChat } from '@/components/assistant/assistant-chat-context';
import { InventoryLifecycleImpact } from '@/components/admin/inventory-lifecycle-impact';
import { AssistantText } from '@/components/assistant/assistant-text';
import { AssistantInputForm } from '@/components/assistant/assistant-input-form';
import { useAssistantSpeech } from '@/components/assistant/use-assistant-speech';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useEntityFeatures } from '@/hooks/use-entity-features';
import { tenantPath } from '@/features/tenancy/urls';

export function proposalReviewLines(action: AssistantProposedAction): Array<{ id: string; text: string }> {
  if (action.type === 'set_timed_plan' && Array.isArray(action.payload.stops)) {
    return action.payload.stops.slice(0, 8).flatMap((value) => {
      if (!value || typeof value !== 'object') return [];
      const stop = value as Record<string, unknown>;
      const links = Array.isArray(stop.bookingIds) ? stop.bookingIds.length : 0;
      const when = typeof stop.startAt === 'string' && typeof stop.endAt === 'string'
        ? formatDateTimeRange(stop.startAt, stop.endAt, { kind: 'site', timeZone: typeof stop.timezone === 'string' ? stop.timezone : null }, { includeZone: true })
        : 'Time unavailable';
      return [{ id: String(stop.id), text: `${String(stop.siteName ?? 'Commons site')}: ${when}${links ? ` · ${links} linked booking(s)` : ''}` }];
    });
  }
  if (action.type === 'decide_location_needs' && Array.isArray(action.payload.items)) {
    return action.payload.items.slice(0, 20).flatMap((value) => {
      if (!value || typeof value !== 'object') return [];
      const need = value as Record<string, unknown>;
      return [{ id: String(need.id), text: `${String(need.featureCode ?? 'Feature')}: ${String(need.decision ?? '')}${need.decision === 'approved' ? ` as ${String(need.strength ?? 'preferred')}` : ''}` }];
    });
  }
  return [];
}

export function AssistantChat({
  className,
  compact = false
}: {
  className?: string;
  /** Tighter layout for the side drawer. */
  compact?: boolean;
}) {
  const {
    messages,
    input,
    setInput,
    error,
    actingToken,
    dismissedTokens,
    isChatPending,
    isConfirmPending,
    sendMessage,
    sendText,
    onConfirm,
    onDecline
  } = useAssistantChat();

  const { isEnabled, tenantSlug } = useEntityFeatures();
  const aiEnabled = isEnabled('ai_tools');
  const endRef = React.useRef<HTMLDivElement>(null);
  const speech = useAssistantSpeech((transcript) => {
    setInput((current) => [current.trim(), transcript].filter(Boolean).join(' ').slice(0, 4000));
  });

  React.useEffect(() => {
    if (!aiEnabled || isChatPending) speech.stopListening(true);
  }, [aiEnabled, isChatPending, speech.stopListening]);

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isChatPending, isConfirmPending]);

  const starterPrompts = [
    'Find a hot desk tomorrow 9–11am',
    'When is my team in next week?',
    'Best day next week to meet my team in person?',
    'Book me a desk near a colleague on Thursday',
    'Set me to office Tuesday and Thursday'
  ];

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-3',
        compact ? 'gap-3' : 'gap-4',
        className
      )}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        {messages.length === 0 && aiEnabled ? (
          <div className="space-y-3 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            <p>Try natural language for bookings or workplace planning:</p>
            <div className="flex flex-wrap gap-2">
              {starterPrompts.map((prompt) => (
                <Button
                  key={prompt}
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-auto max-w-full whitespace-normal px-2 py-1 text-left text-xs"
                  disabled={isChatPending}
                  onClick={() => {
                    void sendText(prompt);
                  }}
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </div>
        ) : null}
        {messages.map((m, index) => (
          <div key={m.id} className="flex flex-col gap-2">
            <div
              className={
                m.role === 'user'
                  ? 'ml-auto max-w-[90%] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground'
                  : 'mr-auto max-w-[90%] rounded-lg bg-muted px-3 py-2 text-sm'
              }
            >
              {m.role === 'assistant' ? <AssistantText text={m.content} /> : m.content}
            </div>
            {aiEnabled && m.role === 'assistant' && index === messages.length - 1 && m.requestedFields?.length ? (
              <AssistantInputForm
                fields={m.requestedFields}
                disabled={isChatPending || isConfirmPending}
                onSend={sendText}
              />
            ) : null}
            {aiEnabled && m.role === 'assistant' && speech.playbackAvailable ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="mr-auto h-7 px-2 text-xs"
                aria-label={speech.speakingId === m.id ? 'Stop reading reply' : 'Read reply aloud'}
                onClick={() => speech.toggleSpeaking(m.id, m.content)}
              >
                {speech.speakingId === m.id ? <SquareIcon className="size-3.5" /> : <Volume2Icon className="size-3.5" />}
                {speech.speakingId === m.id ? 'Stop reading' : 'Read aloud'}
              </Button>
            ) : null}
            {aiEnabled && m.role === 'assistant' && m.selectableOptions && m.selectableOptions.length > 0 ? (
              <div
                className="mr-auto flex max-w-[90%] flex-wrap gap-2"
                role="group"
                aria-label="Suggested options"
              >
                {m.selectableOptions.map((opt) => (
                  <Button
                    key={opt.id}
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-auto max-w-full whitespace-normal px-2.5 py-1.5 text-left text-xs"
                    disabled={isChatPending || isConfirmPending}
                    onClick={() => {
                      void sendText(opt.message);
                    }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            ) : null}
            {aiEnabled && m.role === 'assistant' && m.screenHints && m.screenHints.length > 0 ? (
              <div
                className="mr-auto flex max-w-[90%] flex-wrap gap-2"
                role="group"
                aria-label="Suggested screens"
              >
                {m.screenHints.map((hint) => (
                  <Button
                    key={hint.id}
                    asChild
                    size="sm"
                    variant="outline"
                    className="h-auto px-2.5 py-1.5 text-xs"
                  >
                    <Link to={hint.path} title={hint.description ?? undefined}>
                      {hint.label}
                    </Link>
                  </Button>
                ))}
              </div>
            ) : null}
            {m.proposedActions?.map((action) => {
              if (dismissedTokens.has(action.confirmationToken)) return null;
              return (
                <Card
                  key={action.confirmationToken}
                  className="mr-auto max-w-[90%] border-amber-500/40 bg-amber-500/5"
                >
                  <CardHeader className="space-y-1 p-3 pb-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">Needs confirmation</Badge>
                      <span className="text-xs uppercase text-muted-foreground">
                        {action.type.replaceAll('_', ' ')}
                      </span>
                    </div>
                    <CardTitle className="text-sm font-medium">{action.summary}</CardTitle>
                    {proposalReviewLines(action).length ? (
                      <ul className="list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
                        {proposalReviewLines(action).map((line) => <li key={line.id}>{line.text}</li>)}
                      </ul>
                    ) : null}
                    {action.type === 'set_timed_plan' && Array.isArray(action.payload.removedStopIds) &&
                      action.payload.removedStopIds.length > 0 ? (
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        Removed stops may cancel linked bookings after confirmation.
                      </p>
                    ) : null}
                  </CardHeader>
                  {action.lifecycleReview ? <div className="p-3 pt-0"><InventoryLifecycleImpact key={action.lifecycleReview.previewId} preview={action.lifecycleReview} /></div> : null}
                  <CardContent className="flex flex-wrap gap-2 p-3 pt-1">
                    <Button
                      size="sm"
                      disabled={!aiEnabled || (action.lifecycleReview !== undefined && (!action.lifecycleReview.confirmationAvailable || !action.lifecycleReview.bookingImpact.available)) || (isConfirmPending && actingToken === action.confirmationToken)}
                      onClick={() => void onConfirm(action)}
                    >
                      {isConfirmPending && actingToken === action.confirmationToken ? (
                        <Loader2Icon className="size-4 animate-spin" />
                      ) : (
                        <CheckIcon className="size-4" />
                      )}
                      Confirm
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isConfirmPending}
                      onClick={() => onDecline(action)}
                    >
                      <XIcon className="size-4" /> Decline
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ))}
        {isChatPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" /> Thinking…
          </div>
        ) : null}
        <div ref={endRef} />
      </div>

      {error ? (
        <div className="text-sm text-destructive" role="alert">
          <p>{error}</p>
          {tenantSlug ? <Link className="underline" to={tenantPath(tenantSlug, '/work-plan?tab=stops')}>
            Continue in My Work Plan
          </Link> : null}
        </div>
      ) : null}
      {speech.speechError ? <p className="text-sm text-destructive" role="alert">{speech.speechError}</p> : null}

      {aiEnabled ? <form className="flex shrink-0 flex-col gap-2" onSubmit={(e) => {
        speech.stopListening(true);
        void sendMessage(e);
      }}>
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about bookings, team presence, or your work plan…"
            disabled={isChatPending}
            maxLength={4000}
            autoComplete="off"
            aria-label="Message the booking assistant"
          />
        {speech.recognitionAvailable ? (
          <Button
            type="button"
            variant={speech.listening ? 'secondary' : 'outline'}
            disabled={isChatPending}
            aria-label={speech.listening ? 'Stop listening' : 'Dictate message'}
            aria-pressed={speech.listening}
            title={speech.listening ? 'Stop listening' : 'Dictate message'}
            onClick={speech.toggleListening}
          >
            {speech.listening ? <SquareIcon className="size-4" /> : <MicIcon className="size-4" />}
          </Button>
        ) : null}
          <Button type="submit" disabled={isChatPending || !input.trim()} aria-label="Send message">
            <SendIcon className="size-4" />
            {compact ? null : 'Send'}
          </Button>
        </div>
        {speech.listening ? (
          <p className="text-xs text-muted-foreground" role="status">Listening… Stop when finished, review the transcript, then send.</p>
        ) : speech.recognitionAvailable ? (
          <p className="text-xs text-muted-foreground">Dictation uses your browser's speech service and may send audio to its provider. Review before sending.</p>
        ) : (
          <p className="text-xs text-muted-foreground">Voice input is unavailable in this browser. You can still type your message.</p>
        )}
      </form> : <p className="text-muted-foreground text-sm">AI tools is off. Your conversation history remains visible. {tenantSlug ? <Link className="text-primary underline" to={tenantPath(tenantSlug, '/work-plan?tab=stops')}>Continue in My Work Plan</Link> : null}</p>}
    </div>
  );
}
